import * as THREE from 'three';
import { getOrigin, getSectionBounds } from '../config/siteConfig';
import {
  patchMaterialForObjectClip,
  writeObjectClipUniforms,
} from './objectClip';

export const WIDTH_PRESETS = [1, 5, 10, 25, 50];
export const HOLE_LAYER_TYPES = new Set([
  'holes',
  'holesassays',
  'cachedholes',
  'databaseholes',
  'assays',
]);

const SNAP_KEYS = [
  'enabled',
  'orientation',
  'centerEast',
  'centerNorth',
  'centerRl',
  'width',
  'azimuth',
  'clipMode',
  'lookLocked',
  'lookFlip',
  'boxWidthEast',
  'boxWidthNorth',
  'boxWidthRl',
  'clipHoles',
  'clipSolids',
  'showCaps',
  'showGizmo',
  'stepSize',
  'fencePoints',
  'fenceDistance',
  'obliqueNormal',
  'clipParentUuid',
  'clipParentName',
  'clipParentMode',
  'clipParentClosed',
  'clipParentUsedBox',
];

export function mineToScene(east, north, rl, origin = getOrigin()) {
  return new THREE.Vector3(east - origin.east, rl - origin.rl, origin.north - north);
}

export function sceneToMine(x, y, z, origin = getOrigin()) {
  return {
    east: x + origin.east,
    north: origin.north - z,
    rl: y + origin.rl,
  };
}

export function scenePointToMine(point, origin = getOrigin()) {
  if (!point) return null;
  const x = point.x ?? point[0];
  const y = point.y ?? point[1];
  const z = point.z ?? point[2];
  return sceneToMine(x, y, z, origin);
}

export function clamp(value, min, max) {
  const n = Number(value);
  if (!Number.isFinite(n)) return min;
  return Math.min(max, Math.max(min, n));
}

export function wrapAzimuth(deg) {
  let a = Number(deg);
  if (!Number.isFinite(a)) return 0;
  a %= 360;
  if (a < 0) a += 360;
  return a;
}

function horizDist(a, b) {
  const de = (b.east ?? 0) - (a.east ?? 0);
  const dn = (b.north ?? 0) - (a.north ?? 0);
  return Math.hypot(de, dn);
}

export function azimuthFromPoints(a, b) {
  const de = (b.east ?? 0) - (a.east ?? 0);
  const dn = (b.north ?? 0) - (a.north ?? 0);
  if (Math.hypot(de, dn) < 0.05) return 0;
  return wrapAzimuth((Math.atan2(de, dn) * 180) / Math.PI);
}

export function fenceLength(points) {
  if (!points || points.length < 2) return 0;
  let d = 0;
  for (let i = 1; i < points.length; i += 1) d += horizDist(points[i - 1], points[i]);
  return d;
}

export function fenceSample(points, distance) {
  if (!points?.length) return null;
  if (points.length === 1) {
    return { ...points[0], azimuth: 0 };
  }
  const total = fenceLength(points);
  let remain = clamp(distance, 0, Math.max(total, 0));
  for (let i = 1; i < points.length; i += 1) {
    const a = points[i - 1];
    const b = points[i];
    const seg = horizDist(a, b) || 0.001;
    const last = i === points.length - 1;
    if (remain <= seg || last) {
      const t = remain / seg;
      return {
        east: a.east + (b.east - a.east) * t,
        north: a.north + (b.north - a.north) * t,
        rl: a.rl + (b.rl - a.rl) * t,
        azimuth: azimuthFromPoints(a, b),
      };
    }
    remain -= seg;
  }
  const last = points[points.length - 1];
  return { ...last, azimuth: azimuthFromPoints(points[points.length - 2], last) };
}

/** Horizontal unit normal in scene space. Azimuth 0 = N–S plane (constant easting). */
export function sceneNormalFromAzimuth(azimuthDeg, origin = getOrigin()) {
  void origin;
  const az = (wrapAzimuth(azimuthDeg) * Math.PI) / 180;
  const normalEast = Math.cos(az);
  const normalNorth = -Math.sin(az);
  return new THREE.Vector3(normalEast, 0, -normalNorth).normalize();
}

export function resolvedSectionPose(state, origin = getOrigin()) {
  const orientation = state.orientation || 'rl';
  if (orientation === 'fence') {
    const sample = fenceSample(state.fencePoints || [], state.fenceDistance || 0);
    if (sample) {
      return {
        east: sample.east,
        north: sample.north,
        rl: sample.rl,
        azimuth: sample.azimuth,
        orientation: 'azimuth',
      };
    }
  }
  return {
    east: Number(state.centerEast),
    north: Number(state.centerNorth),
    rl: Number(state.centerRl),
    azimuth: wrapAzimuth(state.azimuth),
    orientation,
  };
}

export function sectionCenterScene(state, origin = getOrigin()) {
  const pose = resolvedSectionPose(state, origin);
  return mineToScene(pose.east, pose.north, pose.rl, origin);
}

/**
 * Unit look/normal in scene space. Camera sits on this side of the plane.
 */
export function sectionNormal(state, origin = getOrigin()) {
  const pose = resolvedSectionPose(state, origin);
  const orientation = pose.orientation;
  let n;
  if (orientation === 'east') n = new THREE.Vector3(1, 0, 0);
  else if (orientation === 'north') n = new THREE.Vector3(0, 0, -1);
  else if (orientation === 'rl') n = new THREE.Vector3(0, 1, 0);
  else if (orientation === 'oblique' && state.obliqueNormal) {
    n = new THREE.Vector3(state.obliqueNormal.x, state.obliqueNormal.y, state.obliqueNormal.z);
    if (n.lengthSq() < 1e-8) n.set(1, 0, 0);
    else n.normalize();
  } else if (orientation === 'box') n = new THREE.Vector3(1, 0, 0);
  else n = sceneNormalFromAzimuth(pose.azimuth, origin);
  if (state.lookFlip) n.negate();
  return n;
}

function pushCorridor(planes, center, normal, halfW, clipMode) {
  const n = normal.clone().normalize();
  if (clipMode === 'single') {
    planes.push(new THREE.Plane(n.clone().negate(), n.dot(center)));
    return;
  }
  planes.push(new THREE.Plane(n.clone(), -(n.dot(center) - halfW)));
  planes.push(new THREE.Plane(n.clone().negate(), n.dot(center) + halfW));
}

export function sectionPlanes(state, origin = getOrigin()) {
  if (!state?.enabled) return [];
  const pose = resolvedSectionPose(state, origin);
  const center = mineToScene(pose.east, pose.north, pose.rl, origin);
  const half = Math.max(0.05, Number(state.width) || 0.1) / 2;
  const mode = state.clipMode === 'single' ? 'single' : 'corridor';
  const planes = [];

  if (pose.orientation === 'object') return [];
  if (pose.orientation === 'box') {
    const he = Math.max(0.05, Number(state.boxWidthEast) || half * 2) / 2;
    const hn = Math.max(0.05, Number(state.boxWidthNorth) || half * 2) / 2;
    const hr = Math.max(0.05, Number(state.boxWidthRl) || half * 2) / 2;
    pushCorridor(planes, center, new THREE.Vector3(1, 0, 0), he, 'corridor');
    pushCorridor(planes, center, new THREE.Vector3(0, 0, -1), hn, 'corridor');
    pushCorridor(planes, center, new THREE.Vector3(0, 1, 0), hr, 'corridor');
    return planes;
  }

  pushCorridor(planes, center, sectionNormal({ ...state, lookFlip: false }, origin), half, mode);
  return planes;
}

export function sectionAxisLabel(state) {
  const o = state?.orientation || 'rl';
  if (o === 'east') return 'Easting';
  if (o === 'north') return 'Northing';
  if (o === 'rl') return 'RL';
  if (o === 'azimuth') return `Az ${Math.round(wrapAzimuth(state.azimuth))}°`;
  if (o === 'oblique') return 'Oblique';
  if (o === 'box') return 'Box';
  if (o === 'fence') return 'Fence';
  if (o === 'object') return 'Object';
  return 'Section';
}

export function formatMine(n, digits = 1) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return v.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

export function sectionStatusLine(state) {
  if (!state?.enabled) return 'Section off';
  const pose = resolvedSectionPose(state);
  const w = Math.max(0.1, Number(state.width) || 0.1);
  const mode = state.clipMode === 'single' ? 'cut' : `${w} m`;
  if (pose.orientation === 'rl' || state.orientation === 'rl') {
    return `RL ${formatMine(pose.rl, 0)} · ${mode}`;
  }
  if (pose.orientation === 'east' || state.orientation === 'east') {
    return `E ${formatMine(pose.east, 0)} · ${mode}`;
  }
  if (pose.orientation === 'north' || state.orientation === 'north') {
    return `N ${formatMine(pose.north, 0)} · ${mode}`;
  }
  if (state.orientation === 'box') {
    return `Box · ${mode}`;
  }
  if (state.orientation === 'object') {
    const how =
      state.clipParentMode === 'inside'
        ? 'inside'
        : state.clipParentMode === 'outside'
          ? 'outside'
          : state.clipParentMode === 'above'
            ? 'above'
            : state.clipParentMode === 'below'
              ? 'below'
              : 'object';
    const name = state.clipParentName || 'object';
    return `${how} ${name}`;
  }
  return `${sectionAxisLabel(state)} · ${mode}`;
}

export function sectionFilename(state, projectName) {
  const site = String(projectName || 'section').replace(/\s+/g, '-');
  const pose = resolvedSectionPose(state);
  const w = Math.round(Math.max(0.1, Number(state.width) || 0.1));
  let tag = 'section';
  if (state.orientation === 'rl') tag = `RL${Math.round(pose.rl)}`;
  else if (state.orientation === 'east') tag = `E${Math.round(pose.east)}`;
  else if (state.orientation === 'north') tag = `N${Math.round(pose.north)}`;
  else if (state.orientation === 'azimuth') tag = `Az${Math.round(pose.azimuth)}`;
  else if (state.orientation === 'fence') tag = `fence${Math.round(pose.east)}`;
  else if (state.orientation === 'box') tag = 'box';
  else if (state.orientation === 'object') tag = `obj-${state.clipParentMode || 'clip'}`;
  return `${site}_${tag}_${w}m.png`;
}

export function moveCenterAlongNormal(state, metres, origin = getOrigin()) {
  const n = sectionNormal({ ...state, lookFlip: false }, origin);
  const c = sectionCenterScene(state, origin);
  c.addScaledVector(n, metres);
  const mine = sceneToMine(c.x, c.y, c.z, origin);
  return mine;
}

export function stepSection(state, dir, origin = getOrigin()) {
  const step = Math.max(0.1, Number(state.stepSize) || Number(state.width) || 10);
  const delta = (dir < 0 ? -1 : 1) * step;
  const next = { ...state };
  const o = state.orientation;

  if (o === 'fence') {
    const total = fenceLength(state.fencePoints || []);
    next.fenceDistance = clamp((Number(state.fenceDistance) || 0) + delta, 0, total);
    const sample = fenceSample(next.fencePoints, next.fenceDistance);
    if (sample) {
      next.centerEast = sample.east;
      next.centerNorth = sample.north;
      next.centerRl = sample.rl;
      next.azimuth = sample.azimuth;
    }
    return next;
  }

  if (o === 'east') next.centerEast = Number(state.centerEast) + delta;
  else if (o === 'north') next.centerNorth = Number(state.centerNorth) + delta;
  else if (o === 'rl') next.centerRl = Number(state.centerRl) + delta;
  else if (o === 'box') next.centerEast = Number(state.centerEast) + delta;
  else {
    const moved = moveCenterAlongNormal(state, delta, origin);
    next.centerEast = moved.east;
    next.centerNorth = moved.north;
    next.centerRl = moved.rl;
  }

  const bounds = getSectionBounds();
  next.centerEast = clamp(next.centerEast, bounds.east[0], bounds.east[1]);
  next.centerNorth = clamp(next.centerNorth, bounds.north[0], bounds.north[1]);
  next.centerRl = clamp(next.centerRl, bounds.rl[0], bounds.rl[1]);
  return next;
}

export function lookCameraPose(state, camera, controls, origin = getOrigin()) {
  const center = sectionCenterScene(state, origin);
  const normal = sectionNormal(state, origin);
  const currentTarget = controls?.target;
  const dist = camera?.position && currentTarget
    ? Math.max(40, camera.position.distanceTo(currentTarget))
    : 400;
  const position = center.clone().addScaledVector(normal, dist);
  const up = Math.abs(normal.y) > 0.92
    ? new THREE.Vector3(0, 0, -1)
    : new THREE.Vector3(0, 1, 0);
  return { position, target: center, up };
}

export function gizmoSize(origin = getOrigin()) {
  void origin;
  const b = getSectionBounds();
  const de = (b.east[1] - b.east[0]) || 2000;
  const dn = (b.north[1] - b.north[0]) || 2000;
  const dr = (b.rl[1] - b.rl[0]) || 400;
  return Math.max(400, Math.min(4000, Math.max(de, dn, dr) * 0.85));
}

export function obliqueNormalFromPoints(a, b, c) {
  const ab = b.clone().sub(a);
  const ac = c.clone().sub(a);
  const n = new THREE.Vector3().crossVectors(ab, ac);
  if (n.lengthSq() < 1e-6) return null;
  return n.normalize();
}

export function serializeSection(state) {
  const out = {};
  SNAP_KEYS.forEach((key) => {
    out[key] = state[key];
  });
  return out;
}

export function defaultSectionState(origin = getOrigin()) {
  return {
    enabled: false,
    panelOpen: false,
    orientation: 'rl',
    centerEast: origin.east,
    centerNorth: origin.north,
    centerRl: origin.rl,
    width: 10,
    azimuth: 0,
    clipMode: 'corridor',
    lookLocked: false,
    lookFlip: false,
    boxWidthEast: 80,
    boxWidthNorth: 80,
    boxWidthRl: 20,
    clipHoles: true,
    clipSolids: true,
    showCaps: true,
    showGizmo: true,
    stepSize: 10,
    placeMode: 'idle',
    pendingPoints: [],
    fencePoints: [],
    fenceDistance: 0,
    obliqueNormal: null,
    clipParentUuid: null,
    clipParentName: '',
    clipParentMode: 'inside',
    clipParentClosed: false,
    clipParentUsedBox: true,
  };
}

export function hydrateSection(snap, origin = getOrigin()) {
  const base = defaultSectionState(origin);
  if (!snap || typeof snap !== 'object') return base;
  const next = { ...base, ...snap, placeMode: 'idle', pendingPoints: [] };
  next.enabled = Boolean(snap.enabled);
  next.panelOpen = false;
  next.width = Math.max(0.1, Number(snap.width) || base.width);
  next.azimuth = wrapAzimuth(snap.azimuth);
  next.clipMode = snap.clipMode === 'single' ? 'single' : 'corridor';
  next.fencePoints = Array.isArray(snap.fencePoints) ? snap.fencePoints : [];
  next.pendingPoints = [];
  if (snap.obliqueNormal && Number.isFinite(snap.obliqueNormal.x)) {
    next.obliqueNormal = {
      x: snap.obliqueNormal.x,
      y: snap.obliqueNormal.y,
      z: snap.obliqueNormal.z,
    };
  } else {
    next.obliqueNormal = null;
  }
  next.clipParentUuid = snap.clipParentUuid || null;
  next.clipParentName = snap.clipParentName || '';
  next.clipParentMode = ['inside', 'outside', 'above', 'below'].includes(snap.clipParentMode)
    ? snap.clipParentMode
    : 'inside';
  next.clipParentClosed = Boolean(snap.clipParentClosed);
  next.clipParentUsedBox = snap.clipParentUsedBox !== false;
  return next;
}

export function collectHoleLayerKeys(modelList) {
  const keys = new Set();
  (modelList || []).forEach((section) => {
    (section.models || []).forEach((model) => {
      const type = String(model?.type || '').toLowerCase();
      if (HOLE_LAYER_TYPES.has(type) && model.name) keys.add(model.name);
    });
  });
  return keys;
}

export function isHoleObject(object, holeKeys) {
  let node = object;
  while (node) {
    if (node.userData?.mdvClipKind === 'holes') return true;
    const key = node.userData?.mdvLayerKey;
    if (key && holeKeys?.has(key)) return true;
    const name = String(node.name || '').toLowerCase();
    if (name === 'holes' || name === 'assays') return true;
    node = node.parent;
  }
  return false;
}

function materialsOf(object) {
  if (!object?.material) return null;
  return Array.isArray(object.material) ? object.material : [object.material];
}

function skipClipNode(object) {
  let node = object;
  while (node) {
    if (node.userData?.mdvSkipClip || node.userData?.mdvSectionGizmo || node.userData?.mdvClipSource) return true;
    node = node.parent;
  }
  return false;
}

export function applySceneClipping(scene, planes, { enabled, clipHoles, clipSolids, holeKeys, objectClipOn, objectClipMode }) {
  const planarOn = Boolean(enabled && planes?.length);
  const objectOn = Boolean(enabled && objectClipOn && objectClipMode);
  const active = planarOn || objectOn;
  scene?.traverse((obj) => {
    const mats = materialsOf(obj);
    if (!mats) return;
    if (skipClipNode(obj)) {
      mats.forEach((m) => {
        if (!m) return;
        m.clippingPlanes = [];
        m.clipShadows = false;
        writeObjectClipUniforms(m.userData?.mdvObjUniforms, objectClipMode, false);
      });
      return;
    }
    const hole = isHoleObject(obj, holeKeys);
    const shouldClip = active && (hole ? clipHoles : clipSolids);
    mats.forEach((m) => {
      if (!m) return;
      if (m.isDerivedMaterial || m.isTroikaTextMaterial) return;
      if (m.isShaderMaterial) m.clipping = true;
      m.clippingPlanes = shouldClip && planarOn ? planes : [];
      m.clipShadows = shouldClip && planarOn;
      const u = patchMaterialForObjectClip(m);
      writeObjectClipUniforms(
        u || m.userData?.mdvObjUniforms,
        objectClipMode,
        shouldClip && objectOn
      );
    });
  });
}
